import { createCellKey } from '@/store/reducers/TableWidgetStore';
import {
  fetchLatestTableState,
  fetchTableStateByWidgetId,
} from '@/store/thunks/FetchTable';
import {
  PopulateTableCellsArgs,
  populateTableCellsData,
  PopulateTableCellsError,
  PopulateTableCellsPayload,
  RawCellValue,
} from '@/store/thunks/PopulateTableCellsData';
import {
  tableCellFactory,
  TableCellRecord,
  TableColumn,
  TableRow,
  tableWidgetFactory,
} from '@/models/Table';
import { TableWidgetData, WidgetType } from '@/models/PanelWidgets';
import { ThreadId } from '@/models/Thread';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TableRecommendationsStoreState {
  stateForThreads: Record<ThreadId, TableWidgetData>;
}

const initialState: TableRecommendationsStoreState = {
  stateForThreads: {},
};
export interface CreateTableRecommendationsPayload {
  threadId: ThreadId;
  tableRecommendationsData: TableWidgetData;
}

export interface AddTableRecommendationsPayload {
  currentThreadId: ThreadId;
  recommendations: TableRow[];
}

export interface SyncWithTablePayload {
  threadId: ThreadId;
  tableData: TableWidgetData;
}

export interface BulkUpdateRecommendationsPayload {
  currentThreadId: ThreadId;
  updates: Partial<TableRow>;
}

export interface UpdateTableRecommendationPayload {
  currentThreadId: ThreadId;
  row: TableRow;
}
/*************
 * reducers *
 *************/
export const createTableRecommendationsReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<CreateTableRecommendationsPayload>,
) => {
  state.stateForThreads[action.payload.threadId] =
    action.payload.tableRecommendationsData;
};

export const addTableRecommendationsReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<AddTableRecommendationsPayload>,
) => {
  const { currentThreadId, recommendations } = action.payload;
  const tableRecommendationsData =
    state.stateForThreads[currentThreadId] ??
    tableWidgetFactory({
      widgetType: WidgetType.TABLE_RECOMMENDATIONS,
    });

  tableRecommendationsData.rows = [
    ...tableRecommendationsData.rows,
    ...recommendations,
  ];
};

export const syncWithTableReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<SyncWithTablePayload>,
) => {
  const { threadId, tableData } = action.payload;
  if (!state.stateForThreads[threadId]) {
    state.stateForThreads[threadId] = tableWidgetFactory({
      widgetType: WidgetType.TABLE_RECOMMENDATIONS,
    });
  }

  const recommendationsState = state.stateForThreads[threadId];

  // sync rows
  const corpusIdsInTable = new Set(
    tableData.rows.map((row) => row.paperCorpusId),
  );
  const filteredRecommendations = recommendationsState.rows.filter(
    (row) => !corpusIdsInTable.has(row.paperCorpusId),
  );
  recommendationsState.rows = filteredRecommendations;

  // sync columns
  recommendationsState.columns = tableData.columns;

  // set updated state
  state.stateForThreads[threadId] = recommendationsState;
};

export const populateTableRecommendationsCellsReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<PopulateTableCellsPayload>,
) => {
  const { currentThreadId, cellValues } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }
  const tableCells = createTableCellsFromRawValues(cellValues);
  tableData.cells = {
    ...tableData.cells,
    ...tableCells,
  };
};

export const populateTableRecommendationsCellsLoadingReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<undefined, string, { arg: PopulateTableCellsArgs }>,
) => {
  const { threadId, columns, rows } = action.meta.arg;
  return setLoadingTableCellsReducer(state, {
    payload: { threadId, columns, rows },
    type: 'tableWidgetStore/loadingTableCells',
  });
};

export const setLoadingTableCellsReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<{
    threadId: string;
    columns?: TableColumn[];
    rows?: TableRow[];
  }>,
) => {
  const { threadId, columns = null, rows = null } = action.payload;
  const tableData = state.stateForThreads[threadId];

  if (!tableData) {
    return;
  }

  const tableCells = { ...tableData.cells };
  const tableColumns = columns ?? tableData.columns;
  const tableRows = rows ?? tableData?.rows;

  for (const row of tableRows) {
    for (const column of tableColumns) {
      const cellKey = createCellKey(row.id, column.id);
      tableCells[cellKey] = tableCellFactory({ isLoading: true });
    }
  }
  tableData.cells = tableCells;
};

export const errorTableCellsReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<
    PopulateTableCellsError | undefined,
    string,
    { arg: PopulateTableCellsArgs }
  >,
) => {
  const { threadId, columns = null, rows = null } = action.meta.arg;
  const tableData = state.stateForThreads[threadId];

  if (!tableData) {
    return;
  }

  const tableColumns = columns ?? tableData.columns;
  const tableRows = rows ?? tableData.rows;
  for (const row of tableRows) {
    for (const column of tableColumns) {
      const cell = tableData.cells[createCellKey(row.id, column.id)];
      if (cell) {
        cell.isLoading = false;
        cell.error = action.payload?.errorMessage;
      }
    }
  }
};

export const bulkUpdateRecommendationsReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<BulkUpdateRecommendationsPayload>,
) => {
  const { currentThreadId, updates } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }
  tableData.rows = tableData.rows.map((row) => {
    const updatedRow: TableRow = { ...row, ...updates };
    return updatedRow;
  });
};

export const UpdateTableRecommendationReducer = (
  state: TableRecommendationsStoreState,
  action: PayloadAction<UpdateTableRecommendationPayload>,
) => {
  const { currentThreadId, row: updatedRow } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }

  const existingRowIdx = tableData.rows.findIndex(
    (existingRow) => existingRow.id === updatedRow.id,
  );

  if (existingRowIdx !== -1) {
    tableData.rows.splice(existingRowIdx, 1, updatedRow);
  }
};

/*************
 * selectors *
 *************/
export const getTableRecommendationsStateForThreadSelector = (
  state: TableRecommendationsStoreState,
  threadId: ThreadId | undefined,
): TableWidgetData | null => {
  if (!threadId) {
    return null;
  }
  return state.stateForThreads[threadId];
};

/*************
 * slice *
 *************/
export const tableRecommendationsStoreSlice = createSlice({
  name: 'tableRecommendationsStore',
  initialState,
  reducers: {
    createTableRecommendations: createTableRecommendationsReducer,
    addTableRecommendations: addTableRecommendationsReducer,
    bulkUpdateRecommendations: bulkUpdateRecommendationsReducer,
    updateTableRecommendation: UpdateTableRecommendationReducer,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLatestTableState.fulfilled, syncWithTableReducer)
      .addCase(fetchTableStateByWidgetId.fulfilled, syncWithTableReducer)
      .addCase(
        populateTableCellsData.pending,
        populateTableRecommendationsCellsLoadingReducer,
      )
      .addCase(
        populateTableCellsData.fulfilled,
        populateTableRecommendationsCellsReducer,
      )
      .addCase(populateTableCellsData.rejected, errorTableCellsReducer);
  },
  selectors: {
    getTableRecommendationsStateForThread:
      getTableRecommendationsStateForThreadSelector,
  },
});

export const { getTableRecommendationsStateForThread } =
  tableRecommendationsStoreSlice.selectors;
export const {
  createTableRecommendations,
  addTableRecommendations,
  bulkUpdateRecommendations,
  updateTableRecommendation,
} = tableRecommendationsStoreSlice.actions;
export const tableRecommendationsStoreReducer =
  tableRecommendationsStoreSlice.reducer;

/*************
 * helpers *
 *************/

export const createTableCellsFromRawValues = (
  rawCellsValues: Record<string, RawCellValue>,
): TableCellRecord => {
  const tableCells: TableCellRecord = {};
  for (const cellKey in rawCellsValues) {
    const cellVal = rawCellsValues[cellKey];
    if (cellVal !== null && 'error' in cellVal) {
      tableCells[cellKey] = tableCellFactory({
        error: cellVal?.error ?? 'Missing value',
      });
      continue;
    }
    tableCells[cellKey] = tableCellFactory({
      displayValue: cellVal?.value,
      metadata: cellVal?.metadata,
    });
  }
  return tableCells;
};
